import { useState, useCallback, useMemo } from 'react';
import type { OpenAPIV3 } from 'openapi-types';

interface SchemaViewerProps {
  schema: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject | undefined;
  name?: string;
  required?: boolean;
  depth?: number;
  schemaId?: string;
  collapsedSchemas?: Set<string>;
  onToggleSchema?: (id: string) => void;
  rawSpec?: OpenAPIV3.Document;
  searchQuery?: string;
}

export function SchemaViewer({
  schema,
  name,
  required,
  depth = 0,
  schemaId = 'root',
  collapsedSchemas = new Set(),
  onToggleSchema,
  rawSpec,
  searchQuery,
}: SchemaViewerProps) {
  // Resolve $ref references
  const resolvedSchema = useMemo((): OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject | undefined => {
    if (!schema) return undefined;
    
    // Check if this is a reference object
    const ref = (schema as OpenAPIV3.ReferenceObject).$ref;
    if (ref && rawSpec) {
      // Handle both #/components/... and /components/... style refs
      const refPath = ref.replace(/^#\//, '').split('/').filter(Boolean);
      let current: unknown = rawSpec;
      
      for (const key of refPath) {
        if (current && typeof current === 'object' && key in (current as Record<string, unknown>)) {
          current = (current as Record<string, unknown>)[key];
        } else {
          return schema; // Return original if can't resolve
        }
      }
      
      // Successfully resolved - return the actual schema
      if (current && typeof current === 'object') {
        return current as OpenAPIV3.SchemaObject;
      }
    }
    return schema;
  }, [schema, rawSpec]);

  // Helper to check if text matches search query
  const getMatchRanges = useCallback((text: string): Array<[number, number]> => {
    if (!searchQuery || !text) return [];
    
    const ranges: Array<[number, number]> = [];
    const lowerText = text.toLowerCase();
    
    // Extract search terms (handle quoted phrases and regular terms)
    const terms: string[] = [];
    const quotedRegex = /"([^"]*)"/g;
    let match;
    while ((match = quotedRegex.exec(searchQuery)) !== null) {
      if (match[1]) terms.push(match[1].toLowerCase());
    }
    
    // Add non-quoted terms (filter out operators and filter keywords)
    const cleanQuery = searchQuery
      .replace(/"[^"]*"/g, ' ')
      .replace(/\b(in|out|exact):/g, ' ')
      .replace(/\b(AND|OR)\b/gi, ' ');
    const simpleTerms = cleanQuery.trim().split(/\s+/).filter(t => t.length >= 2);
    terms.push(...simpleTerms.map(t => t.toLowerCase()));
    
    // Find all matches
    for (const term of terms) {
      let pos = 0;
      while ((pos = lowerText.indexOf(term, pos)) !== -1) {
        ranges.push([pos, pos + term.length - 1]);
        pos += term.length;
      }
    }
    
    // Merge overlapping ranges
    if (ranges.length === 0) return [];
    ranges.sort((a, b) => a[0] - b[0]);
    const merged: Array<[number, number]> = [ranges[0]!];
    for (let i = 1; i < ranges.length; i++) {
      const last = merged[merged.length - 1]!;
      const current = ranges[i]!;
      if (current[0] <= last[1] + 1) {
        last[1] = Math.max(last[1], current[1]);
      } else {
        merged.push(current);
      }
    }
    
    return merged;
  }, [searchQuery]);

  // Highlight text component
  const HighlightedText = useCallback(({ text, className }: { text: string; className?: string }) => {
    if (!searchQuery || !text) {
      return <span className={className}>{text}</span>;
    }
    
    const ranges = getMatchRanges(text);
    if (ranges.length === 0) {
      return <span className={className}>{text}</span>;
    }
    
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    
    for (let i = 0; i < ranges.length; i++) {
      const [start, end] = ranges[i]!;
      
      if (start > lastIndex) {
        parts.push(<span key={`pre-${i}`}>{text.slice(lastIndex, start)}</span>);
      }
      
      parts.push(
        <mark key={`mark-${i}`} className="search-highlight">
          {text.slice(start, end + 1)}
        </mark>
      );
      
      lastIndex = end + 1;
    }
    
    if (lastIndex < text.length) {
      parts.push(<span key="end">{text.slice(lastIndex)}</span>);
    }
    
    return <span className={className}>{parts}</span>;
  }, [searchQuery, getMatchRanges]);
  const [localCollapsed, setLocalCollapsed] = useState<Set<string>>(new Set());
  
  const isControlled = onToggleSchema !== undefined;
  const collapsed = isControlled ? collapsedSchemas : localCollapsed;
  const toggle = useCallback((id: string) => {
    if (isControlled) {
      onToggleSchema(id);
    } else {
      setLocalCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }
  }, [isControlled, onToggleSchema]);

  if (!resolvedSchema) return null;

  // If still a ref (couldn't resolve), show it as expandable
  if ('$ref' in resolvedSchema) {
    const refName = resolvedSchema.$ref.split('/').pop() || 'Unknown';
    return (
      <span className="schema-ref">
        <HighlightedText text={refName} className="schema-ref-link" />
      </span>
    );
  }
  
  // Work with resolved schema from here
  const s = resolvedSchema;

  const isCollapsed = collapsed.has(schemaId);
  const indent = depth * 16;
  
  // Compute hasChildren first (needed for getCollapsedPreview)
  const hasChildren = s.properties || 
    ('items' in s && s.items) || 
    s.oneOf || 
    s.anyOf || 
    s.allOf ||
    s.additionalProperties;

  // Get preview of what's inside when collapsed
  const getCollapsedPreview = useCallback((): string | null => {
    if (!isCollapsed || !hasChildren) return null;
    
    if (s.type === 'array' && s.items) {
      if ('$ref' in s.items) {
        return s.items.$ref.split('/').pop() || null;
      }
      const itemType = (s.items as OpenAPIV3.SchemaObject).type;
      if (itemType === 'object') {
        const props = Object.keys((s.items as OpenAPIV3.SchemaObject).properties || {});
        if (props.length > 0) {
          return `{${props.slice(0, 3).join(', ')}${props.length > 3 ? '...' : ''}}`;
        }
      }
      return itemType || null;
    }
    
    if (s.type === 'object' && s.properties) {
      const props = Object.keys(s.properties);
      if (props.length > 0) {
        return `{${props.slice(0, 3).join(', ')}${props.length > 3 ? '...' : ''}}`;
      }
    }
    
    return null;
  }, [isCollapsed, hasChildren, s]);

  const collapsedPreview = getCollapsedPreview();
  
  const getTypeColor = (type: string | undefined): string => {
    switch (type) {
      case 'string': return 'var(--schema-string)';
      case 'integer':
      case 'number': return 'var(--schema-number)';
      case 'boolean': return 'var(--schema-boolean)';
      case 'array': return 'var(--schema-array)';
      case 'object': return 'var(--schema-object)';
      default: return 'var(--text-secondary)';
    }
  };

  const getTypePreview = (schemaObj: OpenAPIV3.SchemaObject): string => {
    if (schemaObj.type === 'array' && schemaObj.items) {
      if ('$ref' in schemaObj.items) {
        const refName = schemaObj.items.$ref.split('/').pop();
        return `array<${refName}>`;
      }
      const itemType = (schemaObj.items as OpenAPIV3.SchemaObject).type || 'any';
      return `array<${itemType}>`;
    }
    if (schemaObj.type === 'object') {
      const props = schemaObj.properties ? Object.keys(schemaObj.properties) : [];
      if (props.length > 0) {
        const preview = props.slice(0, 3).join(', ');
        const more = props.length > 3 ? ` +${props.length - 3}` : '';
        return `object{${preview}${more}}`;
      }
      return 'object';
    }
    return schemaObj.type || 'any';
  };

  const renderType = () => {
    const { type, format, enum: enumValues, nullable, items } = s;
    
    if (enumValues) {
      return (
        <span className="schema-enum">
          {enumValues.slice(0, 3).map((v) => String(v)).join(' | ')}
          {enumValues.length > 3 && ` +${enumValues.length - 3} more`}
        </span>
      );
    }

    const typeStr = Array.isArray(type) ? type.join(' | ') : type || 'any';
    const formatStr = format ? `<${format}>` : '';
    const nullableStr = nullable ? ' | null' : '';

    // For arrays, show the item type
    let arrayTypeStr = '';
    if (type === 'array' && items) {
      if ('$ref' in items) {
        const refName = items.$ref.split('/').pop();
        arrayTypeStr = `<${refName}>`;
      } else {
        const itemSchema = items as OpenAPIV3.SchemaObject;
        const itemType = itemSchema.type || 'any';
        arrayTypeStr = `<${itemType}>`;
      }
    }

    return (
      <span className="schema-type" style={{ color: getTypeColor(Array.isArray(type) ? type[0] : type) }}>
        {typeStr}{arrayTypeStr}{formatStr}{nullableStr}
      </span>
    );
  };

  const renderConstraints = () => {
    const constraints: string[] = [];
    
    if (s.minLength !== undefined) constraints.push(`min: ${s.minLength}`);
    if (s.maxLength !== undefined) constraints.push(`max: ${s.maxLength}`);
    if (s.minimum !== undefined) constraints.push(`≥${s.minimum}`);
    if (s.maximum !== undefined) constraints.push(`≤${s.maximum}`);
    if (s.pattern) constraints.push(`pattern`);
    if (s.minItems !== undefined) constraints.push(`minItems: ${s.minItems}`);
    if (s.maxItems !== undefined) constraints.push(`maxItems: ${s.maxItems}`);
    if (s.uniqueItems) constraints.push('unique');

    if (constraints.length === 0) return null;

    return (
      <span className="schema-constraints">
        {constraints.join(', ')}
      </span>
    );
  };

  const renderProperties = () => {
    if (!s.properties && !s.additionalProperties) return null;

    const properties = s.properties || {};
    const requiredSet = new Set(s.required || []);

    return (
      <div className="schema-properties" style={{ marginLeft: indent }}>
        {Object.entries(properties).map(([propName, propSchema]) => (
          <SchemaViewer
            key={propName}
            name={propName}
            schema={propSchema as OpenAPIV3.SchemaObject}
            required={requiredSet.has(propName)}
            depth={depth + 1}
            schemaId={`${schemaId}.${propName}`}
            collapsedSchemas={collapsed}
            onToggleSchema={toggle}
            rawSpec={rawSpec}
            searchQuery={searchQuery}
          />
        ))}
        {s.additionalProperties && (
          <div className="schema-additional">
            <span className="schema-additional-key">[additionalProperties]</span>
            <SchemaViewer
              schema={s.additionalProperties as OpenAPIV3.SchemaObject}
              depth={depth + 1}
              schemaId={`${schemaId}.additional`}
              collapsedSchemas={collapsed}
              onToggleSchema={toggle}
              rawSpec={rawSpec}
              searchQuery={searchQuery}
            />
          </div>
        )}
      </div>
    );
  };

  const renderArrayItems = () => {
    if (s.type !== 'array' || !('items' in s) || !s.items) return null;

    return (
      <div className="schema-items" style={{ marginLeft: indent }}>
        <SchemaViewer
          schema={('items' in s ? s.items : undefined) as OpenAPIV3.SchemaObject}
          depth={depth + 1}
          schemaId={`${schemaId}[]`}
          collapsedSchemas={collapsed}
          onToggleSchema={toggle}
          rawSpec={rawSpec}
          searchQuery={searchQuery}
        />
      </div>
    );
  };

  const renderOneOfAnyOfAllOf = () => {
    const variants = s.oneOf || s.anyOf || s.allOf;
    if (!variants) return null;

    const label = s.oneOf ? 'oneOf' : s.anyOf ? 'anyOf' : 'allOf';

    return (
      <div className="schema-variants" style={{ marginLeft: indent }}>
        <div className="schema-variant-label">{label}:</div>
        {!isCollapsed && variants.map((variant, idx) => (
          <div key={idx} className="schema-variant">
            <span className="schema-variant-index">{idx + 1}.</span>
            <SchemaViewer
              schema={variant as OpenAPIV3.SchemaObject}
              depth={depth + 1}
              schemaId={`${schemaId}.${label}.${idx}`}
              collapsedSchemas={collapsed}
              onToggleSchema={toggle}
              rawSpec={rawSpec}
              searchQuery={searchQuery}
            />
          </div>
        ))}
      </div>
    );
  };

  const isExpandable = s.type === 'object' || s.type === 'array' || hasChildren;

  return (
    <div className={`schema-node ${depth === 0 ? 'schema-root' : ''}`}>
      <div className="schema-header">
        {isExpandable && hasChildren && (
          <button
            className="schema-toggle"
            onClick={() => toggle(schemaId)}
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? '▶' : '▼'}
          </button>
        )}
        {!isExpandable && <span className="schema-spacer" />}
        
        {name && (
          <span className="schema-name">
            <HighlightedText text={name} />
            {required && <span className="schema-required">*</span>}
          </span>
        )}
        
        {renderType()}
        {renderConstraints()}
        
        {collapsedPreview && (
          <HighlightedText text={collapsedPreview} className="schema-collapsed-preview" />
        )}
        
        {s.readOnly && <span className="schema-badge read-only">read-only</span>}
        {s.writeOnly && <span className="schema-badge write-only">write-only</span>}
        {s.deprecated && <span className="schema-badge deprecated">deprecated</span>}
      </div>
      
      {s.description && (
        <div className="schema-description">
          <HighlightedText text={s.description} />
        </div>
      )}
      
      {s.example !== undefined && (
        <div className="schema-example">
          Example: <code>{JSON.stringify(s.example)}</code>
        </div>
      )}
      
      {s.default !== undefined && (
        <div className="schema-default">
          Default: <code>{JSON.stringify(s.default)}</code>
        </div>
      )}
      
      {!isCollapsed && (
        <>
          {renderProperties()}
          {renderArrayItems()}
          {renderOneOfAnyOfAllOf()}
        </>
      )}
    </div>
  );
}
